
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface CreatorStats {
  followerCount: number;
  shareCount: number;
  isFollowing: boolean;
  loading: boolean;
}

export function useCreatorStats(creatorDbId: string | null, userId: string | null) {
  const [stats, setStats] = useState<CreatorStats>({
    followerCount: 0,
    shareCount: 0,
    isFollowing: false,
    loading: true,
  });
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Fetch follower count
  const fetchFollowerCount = useCallback(async () => {
    if (!creatorDbId) return 0;
    const { count, error } = await supabase
      .from('user_follows')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', creatorDbId);
    if (error) {
      console.error('Error fetching follower count:', error);
      return 0;
    }
    return count || 0;
  }, [creatorDbId]);

  // Check if current user follows this creator
  const checkIsFollowing = useCallback(async () => {
    if (!creatorDbId || !userId) return false;
    const { data, error } = await supabase
      .from('user_follows')
      .select('id')
      .eq('follower_id', userId)
      .eq('following_id', creatorDbId)
      .maybeSingle();
    if (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
    return !!data;
  }, [creatorDbId, userId]);

  // Fetch share count from creators table
  const fetchShareCount = useCallback(async () => {
    if (!creatorDbId) return 0;
    const { data, error } = await supabase
      .from('creators')
      .select('share_count')
      .eq('id', creatorDbId)
      .maybeSingle();
    if (error) {
      console.error('Error fetching share count:', error);
      return 0;
    }
    return data?.share_count || 0;
  }, [creatorDbId]);

  // Initial fetch
  useEffect(() => {
    if (!creatorDbId) {
      setStats(prev => ({ ...prev, loading: false }));
      return;
    }

    let mounted = true;

    const loadStats = async () => {
      const [followerCount, isFollowing, shareCount] = await Promise.all([
        fetchFollowerCount(),
        checkIsFollowing(),
        fetchShareCount(),
      ]);

      if (mounted) {
        setStats({
          followerCount,
          shareCount,
          isFollowing,
          loading: false,
        });
      }
    };

    loadStats();

    return () => {
      mounted = false;
    };
  }, [creatorDbId, userId, fetchFollowerCount, checkIsFollowing, fetchShareCount]);

  // Real-time subscription for follows
  useEffect(() => {
    if (!creatorDbId) return;

    const channel = supabase
      .channel(`creator-follows-${creatorDbId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_follows',
          filter: `following_id=eq.${creatorDbId}`,
        },
        async () => {
          const count = await fetchFollowerCount();
          const following = await checkIsFollowing();
          setStats(prev => ({ ...prev, followerCount: count, isFollowing: following }));
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [creatorDbId, fetchFollowerCount, checkIsFollowing]);

  // Follow / Unfollow
  const toggleFollow = useCallback(async () => {
    if (!creatorDbId || !userId) return;

    if (stats.isFollowing) {
      // Optimistic update
      setStats(prev => ({
        ...prev,
        isFollowing: false,
        followerCount: Math.max(0, prev.followerCount - 1),
      }));

      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', userId)
        .eq('following_id', creatorDbId);

      if (error) {
        console.error('Error unfollowing:', error);
        // Revert on error
        setStats(prev => ({
          ...prev,
          isFollowing: true,
          followerCount: prev.followerCount + 1,
        }));
      }
    } else {
      // Optimistic update
      setStats(prev => ({
        ...prev,
        isFollowing: true,
        followerCount: prev.followerCount + 1,
      }));

      const { error } = await supabase
        .from('user_follows')
        .insert({ follower_id: userId, following_id: creatorDbId });

      if (error) {
        console.error('Error following:', error);
        // Revert on error
        setStats(prev => ({
          ...prev,
          isFollowing: false,
          followerCount: Math.max(0, prev.followerCount - 1),
        }));
      }
    }
  }, [creatorDbId, userId, stats.isFollowing]);

  // Increment share count
  const incrementShareCount = useCallback(async () => {
    if (!creatorDbId) return;

    // Optimistic update
    setStats(prev => ({ ...prev, shareCount: prev.shareCount + 1 }));

    const { error } = await supabase.rpc('increment_share_count', { creator_id: creatorDbId });

    if (error) {
      // Fallback: manual update
      const { data } = await supabase
        .from('creators')
        .select('share_count')
        .eq('id', creatorDbId)
        .maybeSingle();

      const currentCount = data?.share_count || 0;

      const { error: updateError } = await supabase
        .from('creators')
        .update({ share_count: currentCount + 1 })
        .eq('id', creatorDbId);

      if (updateError) {
        console.error('Error incrementing share count:', updateError);
        setStats(prev => ({ ...prev, shareCount: Math.max(0, prev.shareCount - 1) }));
      }
    }
  }, [creatorDbId]);

  return {
    ...stats,
    toggleFollow,
    incrementShareCount,
  };
}
